import { useEffect } from "react";

import { Link, useParams } from "react-router-dom";

import Staff from "../Staff/Staff";
import Roles from "../Roles/Roles";
import Trucks from "../Trucks/Trucks";
import Locations from "../Locations/Locations";
import LocationGroups from "../LocationGroups/LocationGroups";
import classNames from "../../utils/classNames";
import AuthConnections from "./AuthConnections";
import BoxSizeEditor from "../BoxSizeEditor/BoxSizeEditor";
import AdminCredentials from "../AdminCredentials/AdminCredentials";
import CheckInEditor from "../CheckInEditor/CheckInEditor";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import WarehouseLocations from "../WarehouseLocations/WarehouseLocations";
import PartBins from "../PartBins/PartBins";
import PartAssemblyEditor from "../PartAssemblyEditor/PartAssemblyEditor";
import PartCategorySubParts from "../PartCategorySubParts/PartCategorySubParts";


import {
  ClipboardDocumentListIcon as ClipboardDocumentListOutlineIcon,
  MapPinIcon as MapPinOutlineIcon,
  TagIcon as TagOutlineIcon,
  TruckIcon as TruckOutlineIcon,
  UserIcon as UserOutlineIcon,
  CurrencyPoundIcon as CurrencyPoundOutlineIcon,
  LinkIcon as LinkOutlineIcon,
  Square3Stack3DIcon as Square3Stack3DOutlineIcon,
  CubeIcon as CubeOutlineIcon,
  BuildingOfficeIcon as BuildingOfficeOutlineIcon,
  DocumentTextIcon as DocumentTextOutlineIcon,
  PhotoIcon as PhotoOutlineIcon,
  InboxStackIcon as InboxStackOutlineIcon,
  CubeTransparentIcon as CubeTransparentIconOutline,
  AdjustmentsHorizontalIcon as AdjustmentsHorizontalOutlineIcon,
  ClockIcon as ClockOutlineIcon,
  SwatchIcon as SwatchOutlineIcon,
  PencilSquareIcon as PencilSquareOutlineIcon,
  UserGroupIcon as UserGroupOutlineIcon,
  ClipboardDocumentCheckIcon as ClipboardDocumentCheckOutlineIcon,
  ArchiveBoxIcon as ArchiveBoxOutlineIcon,
  GlobeAltIcon as GlobeAltOutlineIcon,
} from "@heroicons/react/24/outline"

import {
  ClipboardDocumentListIcon as ClipboardDocumentListSolidIcon,
  MapPinIcon as MapPinSolidIcon,
  TagIcon as TagSolidIcon,
  TruckIcon as TruckSolidIcon,
  UserIcon as UserSolidIcon,
  CurrencyPoundIcon as CurrencyPoundSolidIcon,
  LinkIcon as LinkSolidIcon,
  Square3Stack3DIcon as Square3Stack3DSolidIcon,
  CubeIcon as CubeSolidIcon,
  BuildingOfficeIcon as BuildingOfficeSolidIcon,
  DocumentTextIcon as DocumentTextSolidIcon,
  PhotoIcon as PhotoSolidIcon,
  InboxStackIcon as InboxStackSolidIcon,
  CubeTransparentIcon,
  AdjustmentsHorizontalIcon as AdjustmentsHorizontalSolidIcon,
  ClockIcon as ClockSolidIcon,
  SwatchIcon as SwatchSolidIcon,
  PencilSquareIcon as PencilSquareSolidIcon,
  UserGroupIcon as UserGroupSolidIcon,
  ClipboardDocumentCheckIcon as ClipboardDocumentCheckSolidIcon,
  ArchiveBoxIcon as ArchiveBoxSolidIcon,
  GlobeAltIcon as GlobeAltSolidIcon,
} from "@heroicons/react/24/solid"
import WarrantyManager from "../WarrantyManager/WarrantyManager";
import EndSlateEditor from "../EndSlate/EndSlateEditor";
import ShippingPolicies from "../ShippingPolicies/ShippingPolicies";
import TemplatePolicies from "../TemplatePolicies/TemplatePolicies";
import AdminShippingProviders from "../AdminShippingProviders/AdminShippingProviders";
import ListingRules from "../ListingRules/ListingRules";
import ListingRestrictions from "../ListingRestrictions/ListingRestrictions";
import RelistingRules from "../RelistingRules/RelistingRules";
import PartImageSettingsEditor from "./PartImageSettings/PartImageSettingsEditor";
import StockNumberFormatEditor from "./StockNumberFormat/StockNumberFormatEditor";
import VatMarginSchemeSettings from "./VatMarginScheme/VatMarginSchemeSettings";
import PartHistoryFieldsEditor from "./PartHistoryFields/PartHistoryFieldsEditor";
import PartTagEditor from "./PartTagEditor/PartTagEditor";
import usePartHistoryEnabled from "../../hooks/usePartHistoryEnabled";
import TemplateEditor from "../TemplateEditor/TemplateEditor";
import ShopifyShippingPolicySync from "../ShopifyShippingPolicySync/ShopifyShippingPolicySync";
import MaterialManager from "../MaterialManager/MaterialManager";
import DepollutionEditor from "../DepollutionEditor/DepollutionEditor";
import DailyChecksEditor from "../DailyChecksEditor/DailyChecksEditor";
import DailyChecksResults from "../DailyChecks/DailyChecksResults";
import TriageEditor from "../TriageEditor/TriageEditor";
import DismantlingBayEditor from "../DismantlingBayEditor/DismantlingBayEditor";
import CodSettings from "../Cod/CodSettings";
import WasteTrackingSettings from "./WasteTrackingSettings";
import TillSettings from "./TillSettings";
import LeadImportDiagnostics from "./LeadImport/LeadImportDiagnostics";
import SendingDomainSettings from "./SendingDomain/SendingDomainSettings";
import PartProTradeSettings from "./PartProTrade/PartProTradeSettings";
import ReplaceEbayPolicy from "./ReplaceEbayPolicy/ReplaceEbayPolicy";


export default function AdminMenu() {
  const { setInfo } = usePageNameContext();
  const { section } = useParams();
  const isAdminSettings = !section || section === "" || section === "admin-credentials";
  const hasTriage = CheckFeatureFlag("Triage");
  const hasDefra = CheckFeatureFlag("DEFRA");
  // Exact Betty flag spelling - matches PartProTradeService.FeatureName in the API and the help article gate
  const hasPartProTrade = CheckFeatureFlag("PartPro Trade");
  const hasTill = CheckFeatureFlag("Till");
  const partHistoryEnabled = usePartHistoryEnabled();

  // Method for returning the correct admin setting page
  const getPanel = () => {
    switch (section) {
      case "admin-credentials":
        return <AdminCredentials />
      case "cod":
        return <CodSettings />
      case "waste-tracking":
        return hasDefra ? <WasteTrackingSettings /> : <AdminCredentials />
      case "till":
        return hasTill ? <TillSettings /> : <AdminCredentials />
      case "lead-import":
        return <LeadImportDiagnostics />
      case "shipping-providers":
        return <AdminShippingProviders />
      case "connections":
        return <AuthConnections />
      case "partpro-trade":
        return hasPartProTrade ? <PartProTradeSettings /> : <AdminCredentials />
      case "sending-domain":
        return <SendingDomainSettings />
      case "box-size-editor":
        return <BoxSizeEditor />
      case "end-slate-editor":
        return <EndSlateEditor />
      case "locations":
        return <Locations />
      case "location-groups":
        return <LocationGroups />
      case "part-assembly-editor":
        return <PartAssemblyEditor />
      case "part-category-sub-parts":
        return <PartCategorySubParts />
      case "check-in-editor":
        return <CheckInEditor />
      case "depollution-editor":
        return <DepollutionEditor />
      case "daily-checks-editor":
        return <DailyChecksEditor />
      case "daily-checks":
        return <DailyChecksResults />
      case "triage-editor":
        return <TriageEditor />
      case "dismantling-bay-editor":
        return <DismantlingBayEditor />
      case "staff":
        return <Staff />
      case "roles":
        return <Roles />
      case "trucks":
        return <Trucks />
      case "warehouse-locations":
        return <WarehouseLocations />
      case "part-bins":
        return <PartBins />
      case "warranty-manager":
        return <WarrantyManager />
      case "materials":
        return <MaterialManager />
      case "shipping-policies":
        return <ShippingPolicies />
      case "template-policies":
        return <TemplatePolicies />
      case "template-editor":
        return <TemplateEditor />
      case "shopify-shipping-policy-sync":
        return <ShopifyShippingPolicySync />
      case "listing-rules":
        return <ListingRules />
      case "listing-restrictions":
        return <ListingRestrictions />
      case "relisting-rules":
        return <RelistingRules />
      case "replace-ebay-policy":
        return <ReplaceEbayPolicy />
      case "part-image-settings":
        return <PartImageSettingsEditor />
      case "part-tags":
        return <PartTagEditor />
      case "stock-number-format":
        return <StockNumberFormatEditor />
      case "vat-margin-scheme":
        return <VatMarginSchemeSettings />
      case "part-history-fields":
        // Gated as well as hidden from the sub-nav, or a bookmarked link still reaches it
        return partHistoryEnabled ? <PartHistoryFieldsEditor /> : <AdminCredentials />
      default:
        return <AdminCredentials />
    }
  };

  // This is the sub-menu inside of the admin menu. Admin Settings is the landing page so stays
  // pinned first; everything else is sorted alphabetically, so new entries can go anywhere below
  const subNavigation = () => {
    const sections = [
      { icon: PencilSquareOutlineIcon, filledIcon: PencilSquareSolidIcon, name: "COD", href: "cod" },
      ...(hasDefra ? [{ icon: ClipboardDocumentCheckOutlineIcon, filledIcon: ClipboardDocumentCheckSolidIcon, name: "Waste Tracking", href: "waste-tracking" }] : []),
      ...(hasTill ? [{ icon: CurrencyPoundOutlineIcon, filledIcon: CurrencyPoundSolidIcon, name: "Till", href: "till" }] : []),
      { icon: InboxStackOutlineIcon, filledIcon: InboxStackSolidIcon, name: "Lead Import", href: "lead-import" },
      { icon: CubeTransparentIconOutline, filledIcon: CubeTransparentIcon, name: "Shipping Providers", href: "shipping-providers" },
      { icon: LinkOutlineIcon, filledIcon: LinkSolidIcon, name: "Auth Connections", href: "connections" },
      ...(hasPartProTrade ? [{ icon: TagOutlineIcon, filledIcon: TagSolidIcon, name: "PartPro Trade", href: "partpro-trade" }] : []),
      { icon: GlobeAltOutlineIcon, filledIcon: GlobeAltSolidIcon, name: "Sending Domain", href: "sending-domain" },
      { icon: CubeOutlineIcon, filledIcon: CubeSolidIcon, name: "Box Size Editor", href: "box-size-editor" },
      { icon: PhotoOutlineIcon, filledIcon: PhotoSolidIcon, name: "End Slate Editor", href: "end-slate-editor" },
      { icon: AdjustmentsHorizontalOutlineIcon, filledIcon: AdjustmentsHorizontalSolidIcon, name: "Part Image Settings", href: "part-image-settings" },
      { icon: TagOutlineIcon, filledIcon: TagSolidIcon, name: "Part Tags", href: "part-tags" },
      ...(partHistoryEnabled ? [{ icon: ClockOutlineIcon, filledIcon: ClockSolidIcon, name: "Part History Fields", href: "part-history-fields" }] : []),
      { icon: TagOutlineIcon, filledIcon: TagSolidIcon, name: "Stock Number Format", href: "stock-number-format" },
      { icon: CurrencyPoundOutlineIcon, filledIcon: CurrencyPoundSolidIcon, name: "VAT / Margin Scheme", href: "vat-margin-scheme" },
      { icon: MapPinOutlineIcon, filledIcon: MapPinSolidIcon, name: "Yard Locations", href: "locations" },
      { icon: Square3Stack3DOutlineIcon, filledIcon: Square3Stack3DSolidIcon, name: "Yard Sections", href: "location-groups" },
      { icon: Square3Stack3DOutlineIcon, filledIcon: Square3Stack3DSolidIcon, name: "Part Assembly Editor", href: "part-assembly-editor" },
      { icon: CubeOutlineIcon, filledIcon: CubeSolidIcon, name: "Part Category Sub-Parts", href: "part-category-sub-parts" },
      { icon: ClipboardDocumentListOutlineIcon, filledIcon: ClipboardDocumentListSolidIcon, name: "Check In Editor", href: "check-in-editor" },
      { icon: ClipboardDocumentListOutlineIcon, filledIcon: ClipboardDocumentListSolidIcon, name: "Depollution Editor", href: "depollution-editor" },
      { icon: ClipboardDocumentCheckOutlineIcon, filledIcon: ClipboardDocumentCheckSolidIcon, name: "Daily Checks Editor", href: "daily-checks-editor" },
      { icon: ClipboardDocumentCheckOutlineIcon, filledIcon: ClipboardDocumentCheckSolidIcon, name: "Daily Checks", href: "daily-checks" },
      ...(hasTriage ? [{ icon: ClipboardDocumentListOutlineIcon, filledIcon: ClipboardDocumentListSolidIcon, name: "Triage Editor", href: "triage-editor" }] : []),
      { icon: Square3Stack3DOutlineIcon, filledIcon: Square3Stack3DSolidIcon, name: "Dismantling Bay Editor", href: "dismantling-bay-editor" },
      { icon: UserOutlineIcon, filledIcon: UserSolidIcon, name: "Staff", href: "staff" },
      { icon: UserGroupOutlineIcon, filledIcon: UserGroupSolidIcon, name: "Roles", href: "roles" },
      { icon: TruckOutlineIcon, filledIcon: TruckSolidIcon, name: "Trucks", href: "trucks" },
      { icon: BuildingOfficeOutlineIcon, filledIcon: BuildingOfficeSolidIcon, name: "Warehouse Locations", href: "warehouse-locations" },
      { icon: ArchiveBoxOutlineIcon, filledIcon: ArchiveBoxSolidIcon, name: "Part Bins", href: "part-bins" },
      { icon: DocumentTextOutlineIcon, filledIcon: DocumentTextSolidIcon, name: "Warranty Manager", href: "warranty-manager" },
      { icon: SwatchOutlineIcon, filledIcon: SwatchSolidIcon, name: "Materials", href: "materials" },
      { icon: InboxStackOutlineIcon, filledIcon: InboxStackSolidIcon, name: "Shipping Policies", href: "shipping-policies" },
      { icon: TagOutlineIcon, filledIcon: TagSolidIcon, name: "Template Policies", href: "template-policies" },
      { icon: DocumentTextOutlineIcon, filledIcon: DocumentTextSolidIcon, name: "Template Editor", href: "template-editor" },
      { icon: InboxStackOutlineIcon, filledIcon: InboxStackSolidIcon, name: "Shopify Shipping Sync", href: "shopify-shipping-policy-sync" },
      { icon: ClipboardDocumentListOutlineIcon, filledIcon: ClipboardDocumentListSolidIcon, name: "Listing Rules", href: "listing-rules" },
      { icon: ClipboardDocumentListOutlineIcon, filledIcon: ClipboardDocumentListSolidIcon, name: "Listing Restrictions", href: "listing-restrictions" },
      { icon: InboxStackOutlineIcon, filledIcon: InboxStackSolidIcon, name: "Replace eBay Policy", href: "replace-ebay-policy" },
      // { icon: ClipboardDocumentListOutlineIcon, filledIcon: ClipboardDocumentListSolidIcon, name: "Relisting Rules", href: "relisting-rules" },
    ].sort((a, b) => a.name.localeCompare(b.name));

    return [
      { icon: ClipboardDocumentListOutlineIcon, filledIcon: ClipboardDocumentListSolidIcon, name: "Admin Settings", href: "" },
      ...sections,
    ]
  };

  useEffect(() => {
    setInfo({
      name: "Admin Settings",
      desc: "Manage admin-level settings and options",
    });
  }, []);

  return (
    <>
      <div className="mt-4 bg-white rounded-lg shadow">
        <div className="min-h-[256px] divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
          <aside className="py-6 lg:col-span-2">
            <nav className="space-y-1">
              {subNavigation().map((item) => {
                let currentSection = section ? section : "";
                let current = item.href === currentSection;
                return (
                  <Link
                    key={item.name}
                    to={`${item.href !== "" ? `${item.href}` : ""}`}
                    className={classNames(
                      current
                        ? "bg-primary-50 border-primary-500 text-primary-700 hover:bg-primary-50 hover:text-primary-700"
                        : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900",
                      "group border-l-4 px-3 py-2 flex items-center text-sm font-medium"
                    )}
                    aria-current={current ? "page" : undefined}
                  >
                    {current ? (
                      <item.filledIcon
                        className={classNames(
                          "text-primary-700 group-hover:text-primary-700",
                          "flex-shrink-0 -ml-1 mr-3 h-6 w-6"
                        )}
                        aria-hidden="true"
                      />
                    ) : (
                      <item.icon
                        className={classNames(
                          "text-gray-400 group-hover:text-gray-500",
                          "flex-shrink-0 -ml-1 mr-3 h-6 w-6"
                        )}
                        aria-hidden="true"
                      />
                    )}
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </aside>
          <div className="bg-gray-50 rounded-r-lg lg:col-span-10">
            <div
              className={classNames(
                "h-full",
                isAdminSettings ? "" : "py-6 px-4 sm:p-6 lg:pb-8"
              )}
            >
              {getPanel()}
            </div>
          </div>
        </div>
      </div>
    </>
  )

}